// store/slices/productSlice.ts
import { Product, productsApi } from "@/services/productsApi";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface ProductState {
  products: Product[];
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  selectedProduct: Product | null;
  loading: boolean;
  error: string | null;
  updating: boolean;
}

const initialState: ProductState = {
  products: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  },
  selectedProduct: null,
  loading: false,
  error: null,
  updating: false,
};

export const fetchProducts = createAsyncThunk(
  "products/fetchAll",
  async ({
    page,
    limit,
    search,
  }: {
    page: number;
    limit: number;
    search: string;
  }) => {
    const response = await productsApi.getAll(page, limit, search);
    return response;
  },
);

export const fetchProductById = createAsyncThunk(
  "products/fetchById",
  async (id: number) => {
    const { product } = await productsApi.getById(id);
    return product;
  },
);

export const createProduct = createAsyncThunk(
  "products/create",
  async (data: {
    name: string;
    sku: string;
    hsnCode: string;
    price: number;
    packQuantity: number;
    unitId: number;
    gstPercent: string;
  }) => {
    const { product } = await productsApi.create(data);
    return product;
  },
);

export const updateProduct = createAsyncThunk(
  "products/update",
  async ({
    id,
    data,
  }: {
    id: number;
    data: Partial<
      Omit<
        Product,
        "id" | "organizationId" | "createdBy" | "createdAt" | "updatedAt"
      >
    >;
  }) => {
    const { product } = await productsApi.update(id, data);
    return product;
  },
);

export const deleteProduct = createAsyncThunk(
  "products/delete",
  async (id: number) => {
    await productsApi.delete(id);
    return id;
  },
);

const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSelected: (state) => {
      state.selectedProduct = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.products;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch products";
      })
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProduct = action.payload;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Product not found";
      })
      .addCase(createProduct.pending, (state) => {
        state.updating = true;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.updating = false;
        state.products.unshift(action.payload);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Failed to create product";
      })
      .addCase(updateProduct.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.updating = false;
        const index = state.products.findIndex(
          (p) => p.id === action.payload.id,
        );
        if (index !== -1) state.products[index] = action.payload;
        if (state.selectedProduct?.id === action.payload.id)
          state.selectedProduct = action.payload;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Failed to update product";
      })
      .addCase(deleteProduct.pending, (state) => {
        state.updating = true;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.updating = false;
        state.products = state.products.filter((p) => p.id !== action.payload);
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Failed to delete product";
      });
  },
});

export const { clearError, resetSelected } = productSlice.actions;
export default productSlice.reducer;
